import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Spinner } from '@hakimo-ui/shared/ui-base';
import { PauseCircleIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';
import {
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';
import { useClockPosition } from './useClockPosition';
import { formatDateInTimezone } from './utils';

interface Props {
  viewStart: number;
  viewEnd: number;
}

const clampPercent = (p: number) => Math.max(0, Math.min(100, p));

export function TimelineSeeker(props: Props) {
  const { viewStart, viewEnd } = props;
  const { location, clock } = useAlarmGroupStatic();
  const { seekerTimestamp, isSeekerPaused } = useAlarmGroupTime();
  const { isBuffering } = useAlarmGroupPlayback();

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: 'timeline-seeker',
    });

  const lineRef = useRef<HTMLDivElement | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);

  // Compose dnd-kit's node ref with ours so the rAF loop can drive the line.
  const setLineRef = (node: HTMLDivElement | null) => {
    lineRef.current = node;
    setNodeRef(node);
  };

  // Drive position from the clock at 60fps (paused while dragging, so the dnd
  // transform owns movement). The line clamps to the edge; the label additionally
  // clamps so it stays fully inside the timeline at the extremes.
  useClockPosition(clock, viewStart, viewEnd, !isDragging, (pct) => {
    const p = clampPercent(pct);
    if (lineRef.current) lineRef.current.style.left = `${p}%`;
    if (labelRef.current) {
      labelRef.current.style.left = `clamp(0px, calc(${p}% - 2.5rem), calc(100% - 5rem))`;
    }
  });

  // Only the dnd drag transform is React-driven now; `left` is owned by the rAF.
  const seekerTransform = {
    transform: CSS.Translate.toString({
      x: transform?.x ?? 0,
      y: 0,
      scaleX: transform?.scaleX ?? 1,
      scaleY: 1,
    }),
  };

  return (
    <>
      <div
        ref={setLineRef}
        {...listeners}
        {...attributes}
        className="absolute z-10 h-[calc(100%+2.5rem)] cursor-grab active:cursor-grabbing"
        style={seekerTransform}
      >
        {/* Vertical Line */}
        <div
          className="bg-onlight-text-3 dark:bg-ondark-text-2 pointer-events-none absolute left-1/2 top-0 w-[1px] -translate-x-1/2 "
          style={{ height: 'calc(100% - 10px)' }}
        />
        {/* Circle at bottom */}
        <div
          className="bg-onlight-line-3/60 dark:bg-ondark-text-2/60 absolute left-1/2 h-5 w-5 -translate-x-1/2 rounded-full"
          style={{ bottom: '0px' }}
        />
        <div
          className="bg-onlight-text-3/60 dark:bg-ondark-text-2/60 pointer-events-none absolute left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
          style={{ bottom: '8px' }}
        />
      </div>
      <div
        ref={labelRef}
        className="group pointer-events-none absolute -top-6 z-10 flex w-20 items-center justify-center gap-px text-[10px]"
        style={seekerTransform}
      >
        {isBuffering && (
          <span className="group-hover:hidden">
            <Spinner className="dark:text-hakimo-yellow text-primary-700 h-3 w-3 animate-spin" />
          </span>
        )}
        {isSeekerPaused && (
          <span>
            <PauseCircleIcon className="h-4 w-4" />
          </span>
        )}
        <span>
          {formatDateInTimezone(
            seekerTimestamp / 1000,
            location?.timezone ?? 'UTC',
            false
          )}
        </span>
      </div>
    </>
  );
}
